/**
 * Logic Flow store for managing visual logic diagrams
 * 
 * This store handles caching and syncing of logic flow data for games,
 * using the GenericStore pattern for persistent caching.
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import { apiService } from "@/lib/api-functions/api-service";

// Logic Flow Node interface
export interface LogicFlowNode {
  id: string;
  type: 'object' | 'dialog' | 'flow';
  position: { x: number; y: number };
  data: {
    itemId: string;
    itemType: 'object' | 'dialog' | 'flow';
    label: string;
    // Additional metadata from the item
    [key: string]: any;
  };
}

// Logic Flow Edge interface
export interface LogicFlowEdge {
  id: string;
  source: string;
  target: string;
  type: 'dialog_flow' | 'relationship' | 'custom';
  label?: string;
  data?: {
    [key: string]: any;
  };
}

// Logic Flow Data Content
export interface LogicFlowDataContent {
  nodes: LogicFlowNode[];
  edges: LogicFlowEdge[];
  viewport?: { x: number; y: number; zoom: number };
}

// Logic Flow interface
export interface LogicFlow {
  id: string;
  game_id: string;
  name: string;
  description?: string;
  data: LogicFlowDataContent;
  folder?: string | null;
  owner?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Store for logic flows, using GenericStore
 */
export const logicFlowStore = storeRegistry.getStore<LogicFlow>({
  key: 'logicFlows',
  ttlMs: 1000 * 60 * 30, // 30 minutes cache
});

/**
 * Fetch logic flows for a specific game from the API and sync the store
 * @param gameId - The game ID to fetch logic flows for
 * @param toast - Optional toast function for error reporting
 */
export async function fetchLogicFlows(
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<LogicFlow[]> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Fetch data from API
    const result = await apiService.fetchLogicFlows(gameId);
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch logic flows');
    }

    const flows = result.data;
    
    // Sync the store with the fetched data
    await logicFlowStore.syncFromRemote(async () => flows as LogicFlow[]);
    return flows as LogicFlow[];
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch logic flows';
    console.error(`[fetchLogicFlows] Error for gameId: ${gameId}:`, err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Fetch a single logic flow with full details
 * @param flowId - The flow ID to fetch
 * @param gameId - The game ID
 * @param toast - Optional toast function for error reporting
 */
export async function fetchLogicFlow(
  flowId: string,
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<LogicFlow> {
  try {
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    const result = await apiService.fetchLogicFlow(flowId, gameId);
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch logic flow');
    }

    return result.data as LogicFlow;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch logic flow';
    console.error(`[fetchLogicFlow] Error for flowId: ${flowId}:`, err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Create a new logic flow via API and sync the store
 * @param flow - The logic flow data to create
 * @param toast - Optional toast function for error reporting
 */
export async function createLogicFlow(
  flow: {
    game_id: string;
    name: string;
    description?: string;
    data?: LogicFlowDataContent;
    folder?: string | null;
    owner?: string | null;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<LogicFlow> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create data via API
    const result = await apiService.createLogicFlow(flow);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create logic flow');
    }

    const newFlow = result.data as LogicFlow;

    // Add the new item to the store
    const currentData = logicFlowStore.getData();
    const updatedData = [...currentData, newFlow]; // Create a new array instead of mutating
    logicFlowStore.setData(updatedData);

    if (toast) {
      toast({
        title: 'Success',
        description: 'Logic flow created successfully',
      });
    }

    return newFlow;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create logic flow';
    console.error("createLogicFlow - error:", err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update an existing logic flow via API and sync the store
 * @param flow - The logic flow data to update
 * @param toast - Optional toast function for error reporting
 */
export async function updateLogicFlow(
  flow: {
    id: string;
    game_id: string;
    name?: string;
    description?: string;
    data?: LogicFlowDataContent;
    folder?: string | null;
    owner?: string | null;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<LogicFlow> {
  // Store the current state for potential rollback
  const currentData = logicFlowStore.getData();
  const existingFlow = currentData.find(f => f.id === flow.id);
  
  // Create optimistic update with current timestamp
  if (existingFlow) {
    const optimisticFlow: LogicFlow = {
      ...existingFlow,
      ...flow,
      updated_at: new Date().toISOString(),
    };
    
    // Update local store immediately for instant UI feedback (optimistic update)
    const optimisticData = currentData.map(f => 
      f.id === flow.id ? optimisticFlow : f
    );
    logicFlowStore.setData(optimisticData);
  }

  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Update data via API
    const result = await apiService.updateLogicFlow(flow);

    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update logic flow');
    }

    const updatedFlow = result.data as LogicFlow;

    // Update the item in the store with server response
    const finalData = logicFlowStore.getData();
    const updatedData = finalData.map(f => 
      f.id === flow.id ? updatedFlow : f
    );
    logicFlowStore.setData(updatedData);

    return updatedFlow;
  } catch (err) {
    // Revert the optimistic update on error
    logicFlowStore.setData(currentData);
    
    const message = err instanceof Error ? err.message : 'Failed to update logic flow';
    console.error("updateLogicFlow - error:", err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Delete a logic flow via API and sync the store
 * @param id - The logic flow ID to delete
 * @param gameId - The game ID
 * @param toast - Optional toast function for error reporting
 */
export async function deleteLogicFlow(
  id: string,
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<void> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Delete data via API
    const result = await apiService.deleteLogicFlow(id, gameId);

    if (!result.success) {
      throw new Error(result.error || 'Failed to delete logic flow');
    }

    // Remove the item from the store
    const currentData = logicFlowStore.getData();
    const updatedData = currentData.filter(flow => flow.id !== id);
    logicFlowStore.setData(updatedData);

    if (toast) {
      toast({
        title: "Success",
        description: "Logic flow deleted successfully",
      });
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to delete logic flow';
    console.error("deleteLogicFlow - error:", err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Get cached logic flows for a specific game
 * @param gameId - The game ID to get logic flows for
 */
export function getCachedLogicFlows(gameId: string): LogicFlow[] {
  const allFlows = logicFlowStore.getData();
  return allFlows.filter(flow => flow.game_id === gameId);
}

/**
 * Clear logic flow cache
 */
export async function clearLogicFlowCache() {
  try {
    await logicFlowStore.clearCache();
    console.log('Logic flow cache cleared successfully');
  } catch (error) {
    console.error('Failed to clear logic flow cache:', error);
  }
}

